import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Pages } from '../../../../common/src/pages.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../../data/client_local_storage.ts';
import { useProfileDrawerOpen } from './profile_drawer_state.ts';

const mainTabPaths: string[] = [Pages.Today, Pages.Organization, Pages.Favorites, Pages.Library].map(
  (page) => `/${page}`,
);
const entranceDelayMs = 600;
const visibleDurationMs = 4000;
const transitionMs = 250;

type TipPhase = 'pre' | 'shown' | 'leaving';

// One-time callout pointing at the header avatar so people upgrading from the old
// Settings tab can find where settings moved (issue #3356). Rendered inside the
// avatar's wrapper, so it can only appear when the avatar itself is visible, and
// only on the four main tabs (not the copies of those pages inside tutorials).
export const SettingsTip = () => {
  const isDrawerOpen = useProfileDrawerOpen();
  const { pathname } = useLocation();
  // Captured once at mount, before this component itself marks the tip seen
  const [isEligible] = useState(() => !localStorageGet(LocalStorageKey.SettingsTipSeen));
  const [phase, setPhase] = useState<TipPhase | undefined>(undefined);
  const shouldFire = isEligible && mainTabPaths.includes(pathname);

  // Opening the drawer by any means teaches the avatar; never show the tip afterward
  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }
    localStorageSet(LocalStorageKey.SettingsTipSeen, true);
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!shouldFire) {
      return;
    }
    let rafId: number | undefined;
    const showTimer = setTimeout(() => {
      // the drawer may have opened (and marked the tip seen) during the entrance delay
      if (localStorageGet(LocalStorageKey.SettingsTipSeen)) {
        return;
      }
      setPhase('pre');
      // double rAF so the entrance transition runs from the 'pre' styles
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          setPhase('shown');
          localStorageSet(LocalStorageKey.SettingsTipSeen, true);
        });
      });
    }, entranceDelayMs);
    const leaveTimer = setTimeout(
      () => setPhase((current) => (current ? 'leaving' : current)),
      entranceDelayMs + visibleDurationMs,
    );
    return () => {
      clearTimeout(showTimer);
      clearTimeout(leaveTimer);
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [shouldFire]);

  // Any tap dismisses; a tap on the avatar itself also opens the drawer
  useEffect(() => {
    if (phase !== 'shown') {
      return;
    }
    const onPointerDown = () => setPhase('leaving');
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'leaving') {
      return;
    }
    const timer = setTimeout(() => setPhase(undefined), transitionMs);
    return () => clearTimeout(timer);
  }, [phase]);

  if (!phase || isDrawerOpen) {
    return null;
  }
  return (
    <TipBubble $shown={phase === 'shown'}>
      <TipHeadline>Looking for settings?</TipHeadline>
      Tap here.
    </TipBubble>
  );
};

const TipBubble = styled.div<{ $shown: boolean }>`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  z-index: 250;
  padding: 10px 14px 11px;
  border-radius: 12px;
  background-color: var(--color-settings-tip-background);
  color: var(--color-settings-tip-text);
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  opacity: ${({ $shown }) => ($shown ? '1' : '0')};
  transform: translateY(${({ $shown }) => ($shown ? '0' : '-8px')});
  transition:
    opacity ${transitionMs}ms ease,
    transform ${transitionMs}ms ease;

  /* tail centered under the 34px avatar (17px center − half of the 10px square) */
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 12px;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: var(--color-settings-tip-background);
    transform: rotate(45deg);
  }
`;

const TipHeadline = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
`;
